import * as React from "react";
import {bindActionCreators} from "redux";
import {connect} from "react-redux";

import {Store} from "../../../project/reducer";
import {Dispatch} from "../../../../../shared/ts/interfaces/dispatch";
import Main from "../../../shared/layout/Main";
import Content from "../../../project/components/content/Content";
import ContentHeader from "../../../project/components/content/ContentHeader";
import ContentHeaderTitle from "../../../project/components/content/ContentHeaderTitle";
import ContentHeaderControls from "../../../project/components/content/ContentHeaderControls";
import {Button} from "../../../shared/button/Button";
import ContentBody from "../../../project/components/content/ContentBody";
import {FormActions} from "../../../../../shared/ts/helpers/create_form_actions";
import {closePanel, openPanel, openTab} from "../../../context_panel/actions/context_panel";
import {Offer} from "../../../shared/models/Offer";
import {fetchOfferDetailForNatives, resetOfferDetailForNative} from "../../../offer/actions/fetch";
import {getDate} from "../../../../../shared/ts/helpers/formatData";
import {noop} from "../../../../../shared/ts/helpers/lodash";
import {createFileName, urlBase64, urlToCroppedBase64} from "../../../../../shared/ts/helpers/urlToCroppedBase64";
import {url} from "../../../../../shared/ts/helpers/routing";
import {RouterProps} from "../../../../../shared/ts/interfaces/router";
import {prepareRoomRanges} from "../../../../../shared/ts/helpers/roomRanges";
import {RequestState} from "../../../../../shared/ts/helpers/util";
import {HocModalix, modalix} from "../../../../../shared/ts/decorators/modalix";
import {INativeCreation, NativeCreationForm} from "../components/NativeCreationForm";
import {
    addMobileNativesFormActions,
    clearAddEditViewState,
    nativesAdd
} from "../../../advertisments/actions/natives/add";
import {INativesForms} from "../../../advertisments/reducers/natives";
import {NativeOffersAutocomplete} from "../../../advertisments/components/NativeOffersAutocomplete";
import {NativeCreationAdModal} from "../components/NativeCreationAdModal";
import {htmlToImgDownload} from "../actions/html_to_img";
import {saveAsHtml} from "../actions/save_as_html";
import {CreationType} from "../../CreationType";

interface IStateProps {
    nativesDetail: INativesForms;
    nativesOfferDetail: Offer | null;
    contextPanelOpened: boolean;
}

interface IActionsProps {
    mobileFormActions: FormActions;
    fetchOfferDetailForNatives: (id: number) => Promise<any>;
    actions: {
        nativesAdd: typeof nativesAdd;
        resetOfferDetailForNative: typeof resetOfferDetailForNative;
        clearAddEditViewState: typeof clearAddEditViewState;
        openPanel: typeof openPanel;
        closePanel: typeof closePanel;
        openTab: typeof openTab;
    };
}

interface IOwnProps extends RouterProps {
    hoc: HocModalix;
}

interface IProps extends IStateProps, IActionsProps, IOwnProps {}

interface IState {
    initialImage: string | Blob;
    initialVendorImage: string | Blob;
}

@modalix(NativeCreationAdModal)
class NativeCreationAddViewC extends React.Component<IProps, IState> {

    constructor(props: IProps) {
        super(props);
        this.state = {
            initialImage: "",
            initialVendorImage: ""
        };
    }

    /**
     * Lifecycle
     */

    public componentDidMount() {
        this.getDefaultFormValues();
    }

    public componentWillUnmount(): void {
        this.props.actions.clearAddEditViewState();
        this.props.actions.resetOfferDetailForNative();
    }

    private getDefaultFormValues = () => {
        const formValues = {
            mobile_offer_price_type: 0,
            mobile_button_text_type: 1
        };

        this.props.mobileFormActions.update(formValues);
    };

    /**
     * Fetch offer and initialize form
     */

    private onSearchedOfferUpdate = async (data: {offer: {option: unknown; value: number} | null}): Promise<void> => {
        if (data.offer != null) {
            await this.props.fetchOfferDetailForNatives(data.offer.value);
            if (this.props.nativesOfferDetail == null) {
                throw new Error("onSearchedOfferUpdate: offer not saved in store")
            }
            this.triggerPopulatingDataToForm(this.props.nativesOfferDetail);
        }
    };

    private triggerPopulatingDataToForm = (offer: Offer): void => {
        const rawFormValues = this.prepareNativeCreationValues(offer);
        this.props.mobileFormActions.update(rawFormValues);

        this.prepareBase64Images(offer); // at the very end, because it is async anyway
    };

    // This supplements `prepareNativeCreationValues`.
    private prepareBase64Images = async (offer: Offer): Promise<void> => {
        const initialImage = await urlBase64(offer.main_image) as string;
        const mobileImage = await urlToCroppedBase64(offer.main_image, 1125,  633);

        const initialVendorImage = await urlBase64(offer.vendor.logo) as string;
        const mobileVendorImage = await urlToCroppedBase64(offer.vendor.logo, 80, 60);

        this.setState({initialImage, initialVendorImage});

        this.props.mobileFormActions.update({
            mobile_image_native: {
                ...this.props.nativesDetail.mobileForm.mobile_image_native,
                selected: `main_${createFileName(offer.name, offer.pk)}`,
                base64value: mobileImage
            },
            desktop_vendor_logo: {
                ...this.props.nativesDetail.mobileForm.desktop_vendor_logo,
                selected: `main_${createFileName(offer.vendor.name, offer.pk)}`,
                base64value: mobileVendorImage
            }
        });
    };

    private prepareNativeCreationValues = (offer: Offer) => {
        return {
            mobile_area: {
                lower: offer.stats.ranges_area_min,
                upper: offer.stats.ranges_area_max
            },
            mobile_construction_end_date: getDate(offer),
            mobile_individual_price_m2: "",
            mobile_name: offer.name,
            mobile_properties_count_for_sale: offer.stats.properties_count_for_sale,
            mobile_ranges_rooms: prepareRoomRanges(offer.stats.rooms_ranges),
            mobile_address: offer.street_name + (offer.street_number ? ` ${offer.street_number}` : ""),
            mobile_region: offer.region.short_name,
            mobile_type: {
                value: offer.type.pk.toString()
            },
            mobile_vendor: offer.vendor.name
        };
    };

    /**
     * Submit form
     */

    private onSubmit = (shouldGenerate: boolean = false, toHtml: boolean = false) => {
        const creation: INativeCreation = {
            ...NativeCreationForm.toJSON(this.props.nativesDetail.mobileForm),
            type: CreationType.NATIVE,
            offer: this.props.nativesOfferDetail && this.props.nativesOfferDetail.pk
        };
        this.handleFormSubmit(creation, shouldGenerate, toHtml);
    };

    private handleFormSubmit = (creation: INativeCreation, shouldGenerate: boolean, toHtml: boolean) => {
        const nativeCreation = {
            ...creation,
            mobile_offer_price_type: 1
        };

        (this.props.actions.nativesAdd(nativeCreation as any) as any) // ANY: actions' types are base on old creatives types
            .then((response: INativeCreation) => {
                if (response != null) {
                   if (shouldGenerate) {
                       if (toHtml) {
                           this.handleSaveHtml(response.mobile_name, response.id);
                       } else {
                           this.handleConvert(response.mobile_name, response.id);
                       }
                   }
                   return response.id;
                }
            })
            .then((id: number) => {
                if (id != null) {
                    const link = url("app:in:creation:native:edit", {id});
                    this.props.router.push(link);
                }
            }
        );
    };

    private handleConvert = (name: string, id: number) => {
        this.openPreviewModal();
        htmlToImgDownload(document.getElementById("v2-ad")!, name, id);

        // do we want to wait?
        window.setTimeout(() => {
            this.props.hoc.modalix.hide();
        }, 3000);
    };

     private handleSaveHtml = (name: string, id: number) => {
        this.openPreviewModal();
        const element = document.getElementById("v2-ad")!;
        saveAsHtml(element, name, id);
        window.setTimeout(() => {
            this.props.hoc.modalix.hide();
        }, 3000);
    };

    /**
     * Callback
     */

    private openPreviewModal = () => {
        const desktopVendorLogo = this.props.nativesOfferDetail ? {base64: "", stored: this.props.nativesOfferDetail.vendor.logo} : this.props.nativesDetail.mobileForm.desktop_vendor_logo;
        const mobileImageNative = this.props.nativesOfferDetail ? {base64: "", stored: this.props.nativesOfferDetail.main_image} : this.props.nativesDetail.mobileForm.mobile_image_native;

        this.props.hoc.modalix.show<any>({
            nativeCreationFormData: {
                ...this.props.nativesDetail.mobileForm,
                desktop_vendor_logo: desktopVendorLogo,
                mobile_image_native: mobileImageNative
            },
            onReject: () => this.props.hoc.modalix.hide()
        });
    };

    private onImageCropped = (key: "mobile_image_native" | "desktop_vendor_logo", image?: string) => {
        const imgOptions = this.getImgOptions(key, image);

        this.props.mobileFormActions.update({
            [key]: {
                ...this.props.nativesDetail.mobileForm[key],
                ...imgOptions
            }
        });
    };

    private getImgOptions = (key: "mobile_image_native" | "desktop_vendor_logo", image?: string) => {
        if (image) {
            return {
                base64value: image
            }
        }

        return {
            base64value: key === "mobile_image_native" ? this.state.initialImage : this.state.initialVendorImage,
            selected: this.props.nativesOfferDetail ? createFileName(this.props.nativesOfferDetail.name, this.props.nativesOfferDetail.pk) : "initial_image"
        };
    }

    /**
     * Render
     */

    private emptyAutocompleteFormValues = {}; // autocomplete does not need `value` entry

    public render() {
        const {request} = this.props.nativesDetail;
        return (
            <Main>
                <Content>
                    <ContentHeader>
                        <ContentHeaderTitle>Reklamy natywne</ContentHeaderTitle>

                        <ContentHeaderControls>
                            <Button
                                className="ttu ph-xxl mr-xl"
                                type="submit"
                                color="primary-ghost"
                                size="sm"
                                onClick={() => this.openPreviewModal()}
                            >
                                Podgląd Reklam
                            </Button>

                            <Button
                                className="ttu ph-xxl mr-xl"
                                type="submit"
                                color="success"
                                size="sm"
                                submitting={request === RequestState.Waiting}
                                onClick={() => this.onSubmit()}
                            >
                                Zapisz Reklamy
                            </Button>

                            <Button
                                className="ttu ph-xxl"
                                type="submit"
                                color="primary"
                                size="sm"
                                submitting={request === RequestState.Waiting}
                                onClick={() => this.onSubmit(true)}
                            >
                                Zapisz i Wygeneruj Reklamy
                            </Button>
                        </ContentHeaderControls>
                    </ContentHeader>

                    <ContentBody>
                        <NativeOffersAutocomplete
                            onSubmit={noop}
                            errors={null}
                            values={this.emptyAutocompleteFormValues}
                            updateFormAction={this.onSearchedOfferUpdate}
                        />

                        <NativeCreationForm
                            onSubmit={() => this.onSubmit(false)}
                            errors={this.props.nativesDetail.errors ? this.props.nativesDetail.errors.fieldErrors : null}
                            values={this.props.nativesDetail.mobileForm}
                            updateFormAction={this.props.mobileFormActions.update}
                            handlePreview={() => this.openPreviewModal()}
                            submitAndGenerate={() => this.onSubmit(true)}
                            submitAndGenerateHtml={() => this.onSubmit(true, true)}
                            updateImageAfterCropping={this.onImageCropped}
                            initialImage={this.state.initialImage}
                        />
                    </ContentBody>
                </Content>
            </Main>
        );
    }
}

export const NativeCreationAddView = connect(mapStateToProps, mapActionsToProps)(NativeCreationAddViewC);


/**
 * Connect
 */

function mapStateToProps(store: Store): IStateProps {
    return {
        nativesDetail: store.adverts.natives.detail,
        nativesOfferDetail: store.adverts.natives.nativesOfferDetail,
        contextPanelOpened: store.adverts.natives.list.contextPanel.opened
    };
}

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        mobileFormActions: bindActionCreators(addMobileNativesFormActions, dispatch),
        fetchOfferDetailForNatives: fetchOfferDetailForNatives(dispatch),
        actions: bindActionCreators({
            nativesAdd,
            openPanel,
            closePanel,
            openTab,
            clearAddEditViewState,
            resetOfferDetailForNative
        }, dispatch)
    };
}
